import React, { useState, useEffect, useRef } from "react";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import { MdDevices } from "react-icons/md";
import Cin7ImportModal from "./Cin7ImportModal";

const DeviceListTab = ({ brandId, brandName, brandSku, protectedFetch }) => {
  const { showToast } = useToast();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ─── CIN7 import modal state ──────────────────────────────────────────────
  const [showCin7Modal, setShowCin7Modal] = useState(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    protectedFetch(
      apiEndpoints.device.listByBrand(brandId),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        setDevices(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        showToast("Failed to load devices", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [brandId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchDevices = () => {
    protectedFetch(
      apiEndpoints.device.listByBrand(brandId),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        setDevices(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        showToast("Failed to load devices", "error");
      });
  };

  const handleImportSuccess = () => {
    fetchedRef.current = false;
    fetchDevices();
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => setShowCin7Modal(true)}
          style={{ borderRadius: 8, fontWeight: 500 }}
        >
          Import from CIN7
        </button>
      </div>

      {devices.length > 0 ? (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow style={{ background: "#f9fafb" }}>
                  <TableCell style={{ fontWeight: 700 }}>SKU</TableCell>
                  <TableCell style={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell style={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell style={{ fontWeight: 700 }}>
                    Shipping Vendor
                  </TableCell>
                  {/* <TableCell style={{ fontWeight: 700 }}>Actions</TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {devices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((device) => (
                    <TableRow key={device.id}>
                      <TableCell>{device.sku || "—"}</TableCell>
                      <TableCell>{device.name || "—"}</TableCell>
                      <TableCell>{device.category || "—"}</TableCell>
                      <TableCell>{device.shipping_vendor || "—"}</TableCell>
                      {/* <TableCell>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          style={{ borderRadius: 6, fontSize: "0.8rem" }}
                        >
                          Edit
                        </button>
                      </TableCell> */}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 25, 100]}
            component="div"
            count={devices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-toolbar": {
                alignItems: "center",
              },
              ".MuiTablePagination-selectLabel": {
                marginBottom: 0,
              },
              ".MuiTablePagination-displayedRows": {
                marginBottom: 0,
              },
            }}
          />
        </Paper>
      ) : (
        <div style={{ textAlign: "center", padding: "48px 16px" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#f3f4f6",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 12,
            }}
          >
            <MdDevices size={28} color="#9ca3af" />
          </div>
          <p style={{ color: "#6b7280", fontWeight: 500, marginBottom: 4 }}>
            No devices found
          </p>
          <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
            Import devices from CIN7 or add them manually
          </p>
        </div>
      )}

      <Cin7ImportModal
        isOpen={showCin7Modal}
        onClose={() => setShowCin7Modal(false)}
        brandSku={brandSku}
        brandName={brandName}
        brandId={brandId}
        protectedFetch={protectedFetch}
        onImportSuccess={handleImportSuccess}
      />
    </>
  );
};

export default DeviceListTab;
